import { useApi } from '@/api';
import {
  ActivityCard,
  KpiCard,
  MultipleKpiCard,
  Page,
  PageSkeleton
} from '@/components';
import { EtherScanPictogram, Heading } from '@blockanalitica/ui';

import { useSearchParams } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import { CurrencyValue, Flex, PercentageValue } from '@blockanalitica/ui';
import HistoricTabs from './HistoricTabs';

export default function SBEPage() {
  const [daysAgo] = useSearchParams('days_ago', '1');
  const [data, error] = useApi({
    url: '/sbe/',
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Page
        title={
          <Heading size="1">
            <Flex gap="xs">
              Smart Burn Engine
              <a
                href={`https://etherscan.io/address/0x0c10ae443ccb4604435ba63da80ccc63311615bc`}
                target="_blank"
                rel="noreferrer">
                <EtherScanPictogram size="0" />
              </a>
            </Flex>
          </Heading>
        }>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Liquidity owned"
            variant="usds"
            data={data}
            field="total_usd"
            options={{ currency: 'USD' }}
            footer={
              <Flex fontSize="0" gap="3xs">
                <PercentageValue value={data.share} /> of total liquidity
              </Flex>
            }
          />
          <KpiCard
            title="SKY owned"
            variant="sky"
            data={data}
            field="balance_sky"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  iconSize: '3',
                  currency: tokensMapping('SKY')
                }
              }
            }}
          />
          <KpiCard
            title="USDS owned"
            variant="usds"
            data={data}
            field="balance_usds"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  iconSize: '3',
                  currency: tokensMapping('USDS')
                }
              }
            }}
          />
        </Flex>
        <HistoricTabs />

        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} width={['100%', '70%']} alignItems="center">
            <MultipleKpiCard
              title={'Impermanent loss/profit'}
              value={
                <CurrencyValue
                  variant="dynamic"
                  value={data.total_usd - data.sbe_total_usd}
                  options={{
                    currency: 'USD',
                    trend: true,
                    notation: 'standard'
                  }}
                />
              }
              footer={[
                {
                  title: 'Deposited Value',
                  value: (
                    <CurrencyValue
                      value={data.sbe_total_usd}
                      options={{ currency: 'USD' }}
                    />
                  )
                },
                {
                  title: 'Current Value',
                  value: (
                    <CurrencyValue
                      value={data.total_usd}
                      options={{ currency: 'USD' }}
                    />
                  )
                },
                {
                  title: 'SKY PnL',
                  value: (
                    <CurrencyValue
                      value={data.impermanent_sky_loss_gain}
                      options={{
                        currency: tokensMapping('SKY'),
                        iconSize: '0',
                        trend:
                          Number(data.impermanent_sky_loss_gain) === 0
                            ? false
                            : true
                      }}
                    />
                  )
                },
                {
                  title: 'USDS PnL',
                  value: (
                    <CurrencyValue
                      value={data.impermanent_usds_loss_gain}
                      options={{
                        currency: tokensMapping('USDS'),
                        iconSize: '0',
                        trend:
                          Number(data.impermanent_usds_loss_gain) === 0
                            ? false
                            : true
                      }}
                    />
                  )
                }
              ]}
            />
          </Flex>
          <Flex gap={['m', 's']} width={['100%', '30%']} alignItems="stretch">
            <KpiCard
              title="SKY accumulated in Treasury"
              variant="sky"
              data={data}
              field="sky_owned"
              options={{
                notation: 'compact',
                valueOptions: {
                  currencyValueOptions: {
                    iconSize: '3',
                    currency: tokensMapping('SKY')
                  }
                }
              }}
            />
          </Flex>
        </Flex>
        <ActivityCard url={'/sbe/activity/'} linkTo="activity" />
      </Page>
    );
  }
  return <PageSkeleton />;
}
